import { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { apiClient, apiClientRaw } from "../../../api/httpClient";
import {
  setLawfirmId,
  setLocationId,
  setLawfirmUserId,
  setIsLegalService,
} from "../../../store/slices/lawFirmSlice";

/**
 * Law-firm identity bootstrap + add-firm workflow (no home data).
 */
export function useLawFirmIdentityBootstrap(user) {
  const dispatch = useDispatch();
  const lawFirmState = useSelector((state) => state.lawfirm || {});
  const { lawfirmId, locationId, lawfirmUserId } = lawFirmState;
  const isLegalService = useSelector((state) => state.lawfirm?.isLegalService);

  const [loading, setLoading] = useState(true);
  const [lawFirmIdState, setLawFirmIdState] = useState(null);
  const [showDialog, setShowDialog] = useState(false);
  const [lawFirmOptions, setLawFirmOptions] = useState([]);
  const [selectedOption, setSelectedOption] = useState(null);

  const [showAddFirmDialog, setShowAddFirmDialog] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedNewFirm, setSelectedNewFirm] = useState(null);
  const [firmLocationsNew, setFirmLocationsNew] = useState([]);
  const [selectedNewLocation, setSelectedNewLocation] = useState(null);
  const [requestMessage, setRequestMessage] = useState("");
  const [requestingAccess, setRequestingAccess] = useState(false);
  const [requestResult, setRequestResult] = useState(null);

  const fetchLawFirmDetails = useCallback(async () => {
    setLoading(true);
    try {
      const response_user = await apiClient(
        `/get-lawfirm-user-id/${user.id}`,
        "GET"
      );
      const fetchedLawfirmUserId = response_user.lawfirm_user_id;
      dispatch(setLawfirmUserId(fetchedLawfirmUserId));

      const response_lawfirmUser = await apiClient(
        `/lawfirm-user-old/${fetchedLawfirmUserId}`,
        "GET"
      );
      const locationIdFetched =
        response_lawfirmUser.law_firm_location.law_firm_location_id;
      dispatch(setLocationId(locationIdFetched));

      if (response_lawfirmUser.is_legal_service) {
        dispatch(setIsLegalService(true));
        const lawfirmsList = await apiClient(
          `/paralegal-lawfirms/${fetchedLawfirmUserId}`,
          "GET"
        );
        if (lawfirmsList.length === 0) {
          setLawFirmIdState(null);
        } else {
          setLawFirmOptions(lawfirmsList);
          setShowDialog(true);
        }
        setLoading(false);
      } else {
        const response_lawfirm = await apiClient(
          `/get-lawfirm-id/${locationIdFetched}`,
          "GET"
        );
        setLawFirmIdState(response_lawfirm.lawfirm_id);
        dispatch(setLawfirmId(response_lawfirm.lawfirm_id));
        setLoading(false);
      }
    } catch (error) {
      console.error(error);
      setLoading(false);
    }
  }, [dispatch, user.id]);

  useEffect(() => {
    const isPopulated = (val) => val !== undefined && val !== null;
    const alreadyPopulated =
      isPopulated(lawfirmId) &&
      isPopulated(locationId) &&
      isPopulated(lawfirmUserId);

    if (alreadyPopulated) {
      setShowDialog(false);
      setLawFirmIdState(lawfirmId);
      setLoading(false);
      return;
    }

    fetchLawFirmDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchLawFirmDetails]);

  const handleLawFirmSelection = () => {
    const selected = lawFirmOptions.find(
      (item) => item.lawfirm_id === selectedOption
    );
    if (!selected) return;
    setLawFirmIdState(selected.lawfirm_id);
    dispatch(setLawfirmId(selected.lawfirm_id));
    dispatch(setLocationId(selected.location_id));
    setShowDialog(false);
  };

  const handleSearchFirms = async (event, value) => {
    if (!value || value.length < 2) {
      setSearchResults([]);
      return;
    }
    setIsSearching(true);
    try {
      const data = await apiClient(
        `/public/law-firm-search/?q=${encodeURIComponent(value)}`,
        "GET"
      );
      setSearchResults(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error("Search failed", e);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  const handleSelectNewFirm = async (event, newValue) => {
    setSelectedNewFirm(newValue);
    setSelectedNewLocation(null);
    setRequestMessage("");
    setRequestResult(null);
    if (newValue?.lawfirm_id) {
      try {
        const locs = await apiClient(
          `/get-locations-by-lawfirm/${newValue.lawfirm_id}`,
          "GET"
        );
        setFirmLocationsNew(Array.isArray(locs) ? locs : []);
      } catch (e) {
        console.error("Failed to fetch locations", e);
        setFirmLocationsNew([]);
      }
    } else {
      setFirmLocationsNew([]);
    }
  };

  const handleRequestAccess = async () => {
    if (!selectedNewFirm || !selectedNewLocation) return;
    setRequestingAccess(true);
    setRequestResult(null);
    try {
      const response = await apiClientRaw("/legal-service-access-requests/", "POST", {
        law_firm_id: selectedNewFirm.lawfirm_id,
        location_id: selectedNewLocation.law_firm_location_id,
        message: requestMessage,
      });
      if (response.status >= 200 && response.status < 300) {
        setRequestResult({
          success: true,
          message: `Request sent to ${selectedNewFirm.name} admins!`,
        });
      } else {
        const error = new Error("Request failed");
        error.response = response;
        throw error;
      }
      setSelectedNewFirm(null);
      setSelectedNewLocation(null);
      setRequestMessage("");
      setSearchResults([]);
    } catch (e) {
      console.error("Request access failed", e);
      const errorMsg = e.response?.data?.error || "Failed to send request.";
      const isDuplicate = e.response?.status === 409;
      setRequestResult({
        success: false,
        message: errorMsg,
        severity: isDuplicate ? "warning" : "error",
      });
    } finally {
      setRequestingAccess(false);
    }
  };

  return {
    loading,
    lawFirmIdState,
    showDialog,
    selectedOption,
    setSelectedOption,
    lawFirmOptions,
    handleLawFirmSelection,
    isLegalService,
    fetchLawFirmDetails,
    showAddFirmDialog,
    setShowAddFirmDialog,
    searchResults,
    isSearching,
    handleSearchFirms,
    handleSelectNewFirm,
    selectedNewFirm,
    firmLocationsNew,
    selectedNewLocation,
    setSelectedNewLocation,
    requestMessage,
    setRequestMessage,
    handleRequestAccess,
    requestingAccess,
    requestResult,
  };
}
